import { getBookingStatusConfig } from './constants/bookingStatus';

export interface BookingStatusItem {
  name: string;
  value: number;
  amount?: number;
  revenue?: number;
  icon?: string;
}

export interface TransformedBookingStatus {
  name: string;
  value: number;
  amount?: number;
  revenue?: number;
  icon: string;
}

/**
 * Transforms booking counts data to include appropriate icons based on status names
 * @param bookingData - Array of booking status items with name, value, and optional icon
 * @returns Transformed array with icons assigned based on status names
 */
export const transformBookingCounts = (bookingData: BookingStatusItem[]): TransformedBookingStatus[] => {
  if (!bookingData || !Array.isArray(bookingData)) {
    return [];
  }

  return bookingData.map(item => {
    const statusConfig = getBookingStatusConfig(item.name);
    return {
      name: statusConfig.name,
      value: item.value || 0,
      amount: item.amount,
      revenue: item.revenue,
      icon: item.icon || statusConfig.icon
    };
  });
};

/**
 * Creates a default booking counts structure with all statuses set to zero
 * @returns Default booking counts array
 */
export const createDefaultBookingCounts = (): TransformedBookingStatus[] => {
  const defaultStatuses = ['confirmed', 'tentative', 'waitlisted', 'cancelled', 'rfq', 'complementary'];
  
  return defaultStatuses.map(status => {
    const statusConfig = getBookingStatusConfig(status);
    return {
      name: statusConfig.name,
      value: 0,
      icon: statusConfig.icon
    };
  });
};

/**
 * Merges booking data with default structure to ensure all statuses are present
 * @param bookingData - Actual booking data from API
 * @returns Complete booking counts with all statuses
 */
export const ensureCompleteBookingCounts = (bookingData: BookingStatusItem[]): TransformedBookingStatus[] => {
  const defaultCounts = createDefaultBookingCounts();
  const transformedData = transformBookingCounts(bookingData);
  
  // Create a map for easy lookup
  const dataMap = new Map(transformedData.map(item => [item.name.toLowerCase(), item]));
  
  // Merge with defaults, using actual data when available
  return defaultCounts.map(defaultItem => {
    const actualItem = dataMap.get(defaultItem.name.toLowerCase());
    return actualItem || defaultItem;
  });
};
